import { useCallback, useEffect, useState } from "react";
import {
  SEARCH_MARK_ACTIVE_CLASS,
  SEARCH_MARK_CLASS,
} from "./searchUtils";

interface SearchNavigation {
  total: number;
  current: number;
  gotoNext: () => void;
  gotoPrev: () => void;
}

export function useDomSearchNavigation(
  containerRef: React.RefObject<HTMLElement>,
  refreshDeps: ReadonlyArray<unknown>,
): SearchNavigation {
  const [total, setTotal] = useState(0);
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    const root = containerRef.current;
    if (!root) {
      setTotal(0);
      setCurrent(0);
      return;
    }
    const marks = root.querySelectorAll(`mark.${SEARCH_MARK_CLASS}`);
    setTotal(marks.length);
    setCurrent(marks.length > 0 ? 1 : 0);
  }, refreshDeps);

  useEffect(() => {
    const root = containerRef.current;
    if (!root) return;
    const marks = root.querySelectorAll(`mark.${SEARCH_MARK_CLASS}`);
    if (current <= 0 || marks.length === 0) {
      marks.forEach((m) => m.classList.remove(SEARCH_MARK_ACTIVE_CLASS));
      return;
    }
    marks.forEach((m, i) => {
      if (i === current - 1) {
        m.classList.add(SEARCH_MARK_ACTIVE_CLASS);
      } else {
        m.classList.remove(SEARCH_MARK_ACTIVE_CLASS);
      }
    });
    const active = marks[current - 1] as HTMLElement | undefined;
    if (active && typeof active.scrollIntoView === "function") {
      active.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [current, total, containerRef]);

  const gotoNext = useCallback(() => {
    setCurrent((prev) => {
      if (total === 0) return 0;
      return prev >= total ? 1 : prev + 1;
    });
  }, [total]);

  const gotoPrev = useCallback(() => {
    setCurrent((prev) => {
      if (total === 0) return 0;
      return prev <= 1 ? total : prev - 1;
    });
  }, [total]);

  return { total, current, gotoNext, gotoPrev };
}
